import { nextOpenSlot, subOptions } from './roster.js';
import type {
  Availability,
  Fill,
  GroupConfig,
  Override,
  PlayerId,
  Session,
  SubKind,
} from './types.js';

export const OUTSIDER_NAME_MAX = 40;
export const NOTE_MAX = 140;

/** Letters (incl. accents), spaces, apostrophes, hyphens and periods — a name, not a payload. */
const OUTSIDER_NAME_RE = /^[\p{L}][\p{L}\p{M} .'\-]*$/u;

export interface OutRequest {
  outPlayerId: PlayerId;
  subKind: SubKind;
  subPlayerId?: PlayerId;
  subName?: string;
  note?: string;
}

export type ValidationFailure = {
  ok: false;
  /** Stable machine code — the UI switches on this, never on the message. */
  error: string;
  message: string;
  status: 400 | 404 | 409;
};

export type ValidationResult = { ok: true; value: OutRequest } | ValidationFailure;

function fail(
  error: string,
  message: string,
  status: ValidationFailure['status'] = 400,
): ValidationFailure {
  return { ok: false, error, message, status };
}

export function normalizeOutsiderName(raw: unknown): string {
  return typeof raw === 'string' ? raw.trim().replace(/\s+/g, ' ') : '';
}

/**
 * Everything the server checks before writing an override (§4). Shared with the web app so the
 * form disables Save for exactly the cases the API would reject.
 *
 * `excludeOutPlayer` skips the "already out" clash on PATCH, where the caller's own override
 * is expected to exist.
 */
export function validateOutRequest(
  group: GroupConfig,
  session: Session | undefined,
  overrides: Override[],
  body: Partial<OutRequest>,
  opts: { excludeOutPlayer?: PlayerId; availabilities?: Availability[] } = {},
): ValidationResult {
  if (!session) return fail('no-such-session', 'That date is not on the schedule.', 404);
  if (session.status !== 'scheduled') {
    return fail('not-scheduled', 'There is no session that week.');
  }

  const outPlayerId = body.outPlayerId;
  if (typeof outPlayerId !== 'string' || !outPlayerId) {
    return fail('invalid-request', 'outPlayerId is required.');
  }
  if (!group.players.some((p) => p.id === outPlayerId)) {
    return fail('unknown-player', 'That player is not in this group.');
  }
  if (!session.playing.includes(outPlayerId)) {
    return fail('not-playing', 'That player is not scheduled to play that night.');
  }

  const forDate = overrides.filter((o) => o.date === session.date);
  if (
    outPlayerId !== opts.excludeOutPlayer &&
    forDate.some((o) => o.outPlayerId === outPlayerId)
  ) {
    return fail('already-out', 'That player is already marked unavailable.', 409);
  }

  // An override always names a sub — there is no "out, nobody covering yet" state (§3).
  if (body.subKind !== 'reserve' && body.subKind !== 'outsider') {
    return fail('sub-required', 'Choose who is subbing for you.');
  }

  const value: OutRequest = { outPlayerId, subKind: body.subKind };

  if (body.subKind === 'reserve') {
    const subPlayerId = body.subPlayerId;
    if (typeof subPlayerId !== 'string' || !subPlayerId) {
      return fail('sub-required', 'Choose who is subbing for you.');
    }
    const allowed = subOptions(group, session, forDate, outPlayerId, opts.availabilities ?? []);
    // On a PATCH the caller's own override is being replaced, so the reserve it currently
    // names is free again — `subOptions` can't know that, so allow it back explicitly.
    const ownOverride = opts.excludeOutPlayer
      ? forDate.find((o) => o.outPlayerId === opts.excludeOutPlayer)
      : undefined;
    const freedByOwnOverride =
      ownOverride?.subKind === 'reserve' && ownOverride.subPlayerId === subPlayerId;

    if (!freedByOwnOverride && !allowed.some((o) => o.playerId === subPlayerId)) {
      if (session.sitting.includes(subPlayerId)) {
        const declined = (opts.availabilities ?? []).some(
          (a) => a.date === session.date && a.playerId === subPlayerId && !a.available,
        );
        // Two different stale views, and they want two different messages: one reserve is
        // busy, the other has said no. Both 409 — the client's list is out of date either way.
        if (declined) {
          return fail('sub-unavailable', "That reserve has said they can't make it.", 409);
        }
        // A reserve can only sub once per session (§6.1).
        return fail('sub-taken', 'That reserve is already covering someone else.', 409);
      }
      return fail('invalid-sub', 'That player is not sitting out this week.');
    }
    value.subPlayerId = subPlayerId;
  } else {
    const subName = normalizeOutsiderName(body.subName);
    if (!subName) return fail('sub-required', "Enter the name of who's subbing.");
    if (subName.length > OUTSIDER_NAME_MAX) {
      return fail('invalid-name', `Keep the name under ${OUTSIDER_NAME_MAX} characters.`);
    }
    if (!OUTSIDER_NAME_RE.test(subName)) {
      return fail('invalid-name', 'Use letters, spaces, hyphens and apostrophes only.');
    }
    value.subName = subName;
  }

  if (body.note !== undefined && body.note !== null && body.note !== '') {
    if (typeof body.note !== 'string' || body.note.length > NOTE_MAX) {
      return fail('invalid-note', `Keep the note under ${NOTE_MAX} characters.`);
    }
    value.note = body.note.trim();
  }

  return { ok: true, value };
}

export interface FillRequest {
  /** Which of the session's empty slots this name takes. Derived here, not sent by the client. */
  slot: number;
  name: string;
}

export type FillResult = { ok: true; value: FillRequest } | ValidationFailure;

/**
 * Everything checked before recording a name against a short-handed session's empty slot (§9.1).
 *
 * Shorter than {@link validateOutRequest} because a fill points at nobody: there is no player
 * going out, no reserve being claimed and no cascade to guard against — just a night that is
 * short and a name to put in it. The one thing with teeth is that the night still *is* short,
 * which is also the check that closes the door on a fifth player.
 *
 * The slot is chosen here rather than sent, so the client never has to know that slots are
 * indexed. Two people recording a name at the same moment therefore pick the same slot and the
 * conditional write settles it — see {@link nextOpenSlot}.
 */
export function validateFillRequest(
  session: Session | undefined,
  fills: Fill[],
  body: Partial<{ name: string }>,
): FillResult {
  if (!session) return fail('no-such-session', 'That date is not on the schedule.', 404);
  if (session.status !== 'scheduled') {
    return fail('not-scheduled', 'There is no session that week.');
  }

  const slot = nextOpenSlot(session, fills);
  if (slot === undefined) {
    return fail('no-open-slot', 'That night already has four players.', 409);
  }

  // The same rules an outsider sub's name goes through, because it is the same kind of thing:
  // a name somebody typed for a player the app knows nothing else about.
  const name = normalizeOutsiderName(body.name);
  if (!name) return fail('name-required', 'Enter the name of the sub filling in.');
  if (name.length > OUTSIDER_NAME_MAX) {
    return fail('invalid-name', `Keep the name under ${OUTSIDER_NAME_MAX} characters.`);
  }
  if (!OUTSIDER_NAME_RE.test(name)) {
    return fail('invalid-name', 'Use letters, spaces, hyphens and apostrophes only.');
  }

  return { ok: true, value: { slot, name } };
}

export interface AvailabilityRequest {
  playerId: PlayerId;
  available: boolean;
}

export type AvailabilityResult =
  | { ok: true; value: AvailabilityRequest }
  | ValidationFailure;

/**
 * Everything the server checks before recording a player's own answer about one date.
 *
 * The one rule with teeth: **a player holding a court slot cannot simply declare themselves
 * out**, because that would leave three on court with nothing recorded about the fourth. That
 * covers both ways of holding one — their own scheduled slot, and somebody else's slot they
 * agreed to cover. Either way they get `sub-required`, the same code the out flow uses, and the
 * UI sends them to name a sub or a replacement instead (§3, "an override always names a sub").
 *
 * Both are only reachable by a client that skipped the flow: the card menu offers a player in
 * either position the picker, never the bare answer.
 */
export function validateAvailabilityRequest(
  group: GroupConfig,
  session: Session | undefined,
  overrides: Override[],
  body: Partial<AvailabilityRequest>,
): AvailabilityResult {
  if (!session) return fail('no-such-session', 'That date is not on the schedule.', 404);
  if (session.status !== 'scheduled') {
    return fail('not-scheduled', 'There is no session that week.');
  }

  const playerId = body.playerId;
  if (typeof playerId !== 'string' || !playerId) {
    return fail('invalid-request', 'playerId is required.');
  }
  if (!group.players.some((p) => p.id === playerId)) {
    return fail('unknown-player', 'That player is not in this group.');
  }
  if (typeof body.available !== 'boolean') {
    return fail('invalid-request', 'available must be true or false.');
  }

  const forDate = overrides.filter((o) => o.date === session.date);
  const covered = forDate.some((o) => o.outPlayerId === playerId);
  const holdsOwnSlot = session.playing.includes(playerId) && !covered;
  const coveringSomeoneElse = forDate.some(
    (o) => o.subKind === 'reserve' && o.subPlayerId === playerId,
  );
  if (!body.available && holdsOwnSlot) {
    return fail('sub-required', 'Choose who is subbing for you.');
  }
  if (!body.available && coveringSomeoneElse) {
    return fail('sub-required', 'Choose who is taking your place.');
  }

  return { ok: true, value: { playerId, available: body.available } };
}
